import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Table,
  Label,
  Badge,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import { Eye } from "react-feather";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* === Use shared modules from ../import (per your rules) === */
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import SuperAdminUI from "../import/SuperAdminUI";

import { _Get, _GetList, Stockupdate } from "../import/ApiEndPoint/ApiCalling";

import {
  view_create_order_history,
  Stock_trx_FtoW_List,
  Warehouse_ListBy_id,
} from "../import/ApiEndPoint/Api";

/* ---------- Tiny page-scoped CSS (injected once) ---------- */
const injectOnce = () => {
  if (document.getElementById("outward-inline-css")) return;
  const s = document.createElement("style");
  s.id = "outward-inline-css";
  s.textContent = `
    .os-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .os-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .os-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1100px; }
    .os-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .os-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .os-table tfoot td{
      padding:8px 10px; font-weight:800; background:#fafafa; border-top:1px solid #e5e7eb; text-align:center;
    }
    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer; }
    .chip-view{ background:#2563eb; }
    .dl-menu{ position:relative; }
    .dl-pop{ position:absolute; right:0; top:32px; z-index:5; border:1px solid #0b6bcb; background:#fff; border-radius:10px; overflow:hidden; min-width:130px; box-shadow:0 12px 24px rgba(0,0,0,.12); }
    .dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
    .dl-item:hover{ background:#f8fafc; }
  `;
  document.head.appendChild(s);
};

/* ---------- Helpers ---------- */
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const fmt2 = (n) => (n || n === 0 ? Number(n).toFixed(2) : "");
const asDate = (s) => (s ? String(s).split("T")[0] : "");
const safeGet = (obj, path, def = "") =>
  path
    .split(".")
    .reduce((a, k) => (a && a[k] !== undefined ? a[k] : undefined), obj) ?? def;

const N = (v) => (isNaN(Number(v)) ? 0 : Number(v));
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const defaultColorConfig = {
  green: "#75fa8e",
  orange: "#d9614c",
  blue: "#87c8fa",
  red: "#f78981",
};

const getProfitColor = (profitPercentage, colorConfig) => {
  const raw = profitPercentage ?? "";
  const normalized = String(raw).replace(/[^\d.-]/g, "");
  const p = normalized === "" ? 0 : Number(normalized) || 0;

  const merged = {
    ...defaultColorConfig,
    ...(colorConfig || {}),
  };

  if (p >= 15) return merged.green;
  if (p >= 10) return merged.orange;
  if (p >= 5) return merged.blue;
  return merged.red;
};

const parseYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const rowDateValue = (r) =>
  safeGet(r, "order.stockTransferDate", r?.order?.createdAt);

export default function OutwardStock() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [masterShow, setMasterShow] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");

  const [colorConfig, setColorConfig] = useState(defaultColorConfig);

  // date filter
  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // column chooser
  const [modal, setModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // detail modal
  const [viewModal, setViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  useEffect(() => injectOnce(), []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("PriceListColorConfig_v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setColorConfig((prev) => ({ ...prev, ...parsed }));
        }
      }
    } catch {
      // ignore
    }
  }, []);

  /* ---------- Columns ---------- */
  const baseColumns = useMemo(
    () => [
      { key: "sno", header: "S.NO", get: (_, i) => i + 1 },
      {
        key: "partyTo",
        header: "PARTY / TO",
        get: (r) =>
          r?.PartyName || safeGet(r, "order.warehouseToId.warehouseName", "-"),
      },
      {
        key: "productName",
        header: "PRODUCT NAME",
        get: (r) => safeGet(r, "productId.Product_Title", "-"),
        render: (r) => {
          const profitPercentage = safeGet(r, "productId.ProfitPercentage", 0);
          return (
            <span
              style={{
                color: getProfitColor(profitPercentage, colorConfig),
                fontWeight: 700,
              }}
            >
              {safeGet(r, "productId.Product_Title", "-")}
            </span>
          );
        },
      },
      {
        key: "hsn",
        header: "HSN",
        get: (r) => safeGet(r, "productId.HSN_Code", "-"),
      },
      {
        key: "qty",
        header: "OUTWARD QTY",
        get: (r) => r?.qty ?? r?.transferQty ?? 0,
        render: (r) => fmt2(r?.qty ?? r?.transferQty ?? 0),
      },
      {
        key: "salesRate",
        header: "SALE RATE",
        get: (r) =>
          Number(r?.Purchase_Rate ?? safeGet(r, "productId.Purchase_Rate", 0)),
        render: (r) =>
          fmt2(r?.Purchase_Rate ?? safeGet(r, "productId.Purchase_Rate", 0)),
      },
      {
        key: "basicTotal",
        header: "BASIC TOTAL",
        get: (r) =>
          round2(
            Number(r?.qty ?? r?.transferQty ?? 0) *
              Number(
                r?.Purchase_Rate ?? safeGet(r, "productId.Purchase_Rate", 0),
              ),
          ),
        render: (r) =>
          fmt2(
            round2(
              Number(r?.qty ?? r?.transferQty ?? 0) *
                Number(
                  r?.Purchase_Rate ?? safeGet(r, "productId.Purchase_Rate", 0),
                ),
            ),
          ),
      },
      {
        key: "gstRate",
        header: "GST RATE",
        get: (r) => Number(safeGet(r, "productId.GSTRate", 0)),
        render: (r) => fmt2(safeGet(r, "productId.GSTRate", 0)),
      },
      {
        key: "total",
        header: "TOTAL",
        get: (r) => Number(r?.Total || 0),
        render: (r) => fmt2(r?.Total || 0),
      },
      {
        key: "date",
        header: "DATE",
        get: (r) => asDate(rowDateValue(r)),
      },
      {
        key: "status",
        header: "STATUS",
        get: (r) =>
          safeGet(r, "order.transferStatus", safeGet(r, "order.status", "-")),
        render: (r) => {
          const st = safeGet(
            r,
            "order.transferStatus",
            safeGet(r, "order.status", "-"),
          );
          const col = /completed|dispatch/i.test(st) ? "success" : "warning";
          return <Badge color={col}>{st}</Badge>;
        },
      },
    ],
    [colorConfig],
  );

  const actionsColumn = useMemo(
    () => ({
      key: "_actions_",
      header: "ACTIONS",
      get: () => "",
      render: (r) => (
        <div className="action-chips">
          {permissions?.View && (
            <span
              className="chip chip-view"
              title="View"
              onClick={() => openViewModal(r)}
            >
              <Eye size={14} color="white" />
            </span>
          )}
        </div>
      ),
    }),
    [permissions],
  );

  const colMap = useMemo(() => {
    const m = new Map();
    baseColumns.forEach((c) => m.set(c.key, c));
    m.set(actionsColumn.key, actionsColumn);
    return m;
  }, [baseColumns, actionsColumn]);

  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("OutwardStockCols");
    let next = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length)
        next = parsed.filter((k) => colMap.has(k));
    } catch {}
    if (!next.length) next = baseColumns.map((c) => c.key);
    setSelectedKeys(next);
  }, [colMap, baseColumns]);

  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn];
  }, [selectedKeys, colMap, actionsColumn]);

  const filteredRows = useMemo(() => {
    let data = rows.slice();

    if (startDate || endDate) {
      const min = parseYMD(startDate) || new Date(-8640000000000000);
      const max = parseYMD(endDate);
      const maxIncl = max
        ? new Date(max.getFullYear(), max.getMonth(), max.getDate() + 1)
        : new Date(8640000000000000);

      data = data.filter((r) => {
        const raw = rowDateValue(r);
        const d = raw ? new Date(raw) : null;
        return d && !isNaN(d.getTime()) && d >= min && d < maxIncl;
      });
    }

    const term = search.trim().toLowerCase();
    if (!term) return data;

    const searchCols = visibleCols.filter((c) => c.key !== "_actions_");
    return data.filter((r, idx) =>
      searchCols.some((c) => toStr(c.get(r, idx)).toLowerCase().includes(term)),
    );
  }, [rows, search, visibleCols, startDate, endDate]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        const qty = Number(r?.qty ?? r?.transferQty ?? 0);
        const salesRate = Number(
          r?.Purchase_Rate ?? safeGet(r, "productId.Purchase_Rate", 0),
        );
        const basicTotal = round2(qty * salesRate);
        const gstRate = Number(safeGet(r, "productId.GSTRate", 0));
        const total = Number(r?.Total || 0);

        acc.qty += qty;
        acc.salesRate += salesRate;
        acc.basicTotal += basicTotal;
        acc.gstRate += gstRate;
        acc.total += total;

        return acc;
      },
      { qty: 0, salesRate: 0, basicTotal: 0, gstRate: 0, total: 0 },
    );
  }, [filteredRows]);

  /* ---------- Data load ---------- */
  const reload = async (id, db, isWarehouseIncharge) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const inside = CheckPermission("Outward Stock");
      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }

      let merged = [];

      try {
        const res = await _Get(view_create_order_history, id, db);
        const dispatches = (res?.orderHistory || []).filter((o) =>
          String(o?.status || "")
            .toLowerCase()
            .includes("dispatch"),
        );

        const salesRows = dispatches.flatMap((ord) =>
          (ord?.orderItems || []).map((it) => {
            const rate =
              it?.qty && it?.taxableAmount
                ? Number((Number(it.taxableAmount) / Number(it.qty)).toFixed(2))
                : Number(it?.price || 0);

            return {
              ...it,
              order: ord,
              PartyName: ord?.partyId?.CompanyName,
              qty: it?.qty,
              Purchase_Rate: rate,
              Total: Number(
                (Number(it?.qty || 0) * Number(rate || 0)).toFixed(2),
              ),
            };
          }),
        );
        merged = merged.concat(salesRows);
      } catch {}

      const trx = await _Get(Stock_trx_FtoW_List, db);
      const completed = (trx?.Warehouse || []).filter(
        (t) => t?.transferStatus === "Completed",
      );

      if (isWarehouseIncharge) {
        const url = `${String(Warehouse_ListBy_id).replace(/\/+$/, "")}/${db}`;
        const wl = await _GetList(url.replace(/\/+$/, "") + `/${id}`);
        const assigned = (wl?.Warehouse || []).map((w) => w?._id);

        const filtered = completed.filter((t) =>
          assigned.some((aw) => String(aw) === String(t?.warehouseFromId?._id)),
        );

        const txRows = filtered.flatMap((t) =>
          (t?.productItems || []).map((pi) => ({
            ...pi,
            order: t,
            qty: pi?.transferQty,
            PartyName: t?.warehouseToId?.warehouseName,
            Purchase_Rate: Number(pi?.productId?.Purchase_Rate || 0),
            Total: Number(
              (
                Number(pi?.transferQty || 0) *
                Number(pi?.productId?.Purchase_Rate || 0)
              ).toFixed(2),
            ),
          })),
        );
        merged = merged.concat(txRows);
      } else {
        const txRows = completed.flatMap((t) =>
          (t?.productItems || []).map((pi) => ({
            ...pi,
            order: t,
            qty: pi?.transferQty,
            PartyName: t?.warehouseToId?.warehouseName,
            Purchase_Rate: Number(pi?.productId?.Purchase_Rate || 0),
            Total: Number(
              (
                Number(pi?.transferQty || 0) *
                Number(pi?.productId?.Purchase_Rate || 0)
              ).toFixed(2),
            ),
          })),
        );
        merged = merged.concat(txRows);
      }

      setRows(merged);
      setAllRows(merged);
    } catch (e) {
      console.error("[OutwardStock] fetch error:", e);
      setErrorMsg(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to load Outward Stock. Check console.",
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const roleName = user?.rolename?.roleName;
      setMasterShow(roleName === "MASTER");
      const isWhIncharge = roleName === "WareHouse Incharge";
      await reload(user?._id, user?.database, isWhIncharge);
    })();
  }, []);

  /* ---------- Column chooser ---------- */
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, key) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("OutwardStockCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  /* ---------- Date filter ---------- */
  const applyDateFilter = () => {
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
  };

  const resetDateFilter = () => {
    setDraftStartDate("");
    setDraftEndDate("");
    setStartDate("");
    setEndDate("");
  };

  /* ---------- Exports ---------- */
  const handleExportPDF = async () => {
    const cols = visibleCols.filter((c) => c.key !== "_actions_");
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r, idx) =>
      cols
        .map((c) => {
          const val =
            c.key === "productName"
              ? safeGet(r, "productId.Product_Title", "-")
              : c.key === "status"
                ? safeGet(
                    r,
                    "order.transferStatus",
                    safeGet(r, "order.status", "-"),
                  )
                : c.get(r, idx);
          return `"${String(val).replace(/"/g, '""')}"`;
        })
        .join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "OutwardStock");
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols.filter((c) => c.key !== "_actions_");
    const data = filteredRows.map((r, idx) => {
      const obj = {};
      cols.forEach((c) => {
        const val =
          c.key === "productName"
            ? safeGet(r, "productId.Product_Title", "-")
            : c.key === "status"
              ? safeGet(
                  r,
                  "order.transferStatus",
                  safeGet(r, "order.status", "-"),
                )
              : c.get(r, idx);
        obj[c.header] = toStr(val);
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  /* ---------- Detail modal ---------- */
  const openViewModal = (row) => {
    const transfer = row?.order || null;
    setViewData(transfer ? transfer : { fromSales: true, row });
    setViewModal(true);
  };

  const closeViewModal = () => {
    setViewModal(false);
    setViewData(null);
  };

  const updateStatus = async (transfer, newStatus) => {
    if (!transfer?._id) return;
    const ok = await swal("Warning", "Sure you want to update status?", {
      buttons: { cancel: "No", catch: { text: "Yes", value: "yes" } },
    });
    if (ok !== "yes") return;
    try {
      setLoading(true);
      await Stockupdate(transfer._id, { transferStatus: newStatus });
      swal("Success", "Status Updated Successfully");
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const isWhIncharge = user?.rolename?.roleName === "WareHouse Incharge";
      await reload(user?._id, user?.database, isWhIncharge);
      closeViewModal();
    } catch (e) {
      console.error(e);
      swal("Update failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadChallanPDF = (transfer) => {
    if (!transfer) return;
    const doc = new jsPDF("p", "pt", "a4");
    const from = transfer?.warehouseFromId?.warehouseName || "-";
    const to = transfer?.warehouseToId?.warehouseName || "-";
    const dt = transfer?.stockTransferDate
      ? asDate(transfer.stockTransferDate)
      : "-";
    const status = transfer?.transferStatus || "-";

    doc.setFontSize(14);
    doc.text("Outward Stock Challan", 40, 40);
    doc.setFontSize(11);
    doc.text(`Transferred From: ${from}`, 40, 70);
    doc.text(`Transferred To: ${to}`, 40, 90);
    doc.text(`Date: ${dt}`, 40, 110);
    doc.text(`Status: ${status}`, 40, 130);

    const headers = [["#", "Product", "Primary Unit", "Qty"]];
    const body = (transfer?.productItems || []).map((p, i) => [
      i + 1,
      safeGet(p, "productId.Product_Title", "-"),
      p?.primaryUnit || "-",
      p?.transferQty ?? "-",
    ]);

    doc.autoTable({
      head: headers,
      body,
      startY: 150,
      styles: { fontSize: 10 },
      theme: "grid",
    });
    doc.save(`OutwardChallan_${from}_to_${to}_${dt}.pdf`);
  };

  /* ---------- MASTER ---------- */
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  const handleMasterSubmit = async () => {
    const saved = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null",
    );
    if (!saved) return;
    const [id, db] = String(saved).split(" ");
    await reload(id, db, false);
  };

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* ===== Toolbar ===== */}
      <Row className="os-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Outward Stock{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 100px" }} />

              {masterShow ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleMasterSubmit}
                  />
                </div>
              ) : null}

              <div
                style={{
                  minWidth: 220,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <div style={{ minWidth: 145 }}>
                <Label className="mb-0" style={{ fontSize: 12 }}>
                  Start Date
                </Label>
                <Input
                  type="date"
                  value={draftStartDate}
                  onChange={(e) => setDraftStartDate(e.target.value)}
                  style={{ background: "#fff", height: 32 }}
                />
              </div>

              <div style={{ minWidth: 145 }}>
                <Label className="mb-0" style={{ fontSize: 12 }}>
                  End Date
                </Label>
                <Input
                  type="date"
                  value={draftEndDate}
                  onChange={(e) => setDraftEndDate(e.target.value)}
                  style={{ background: "#fff", height: 32 }}
                />
              </div>

              <Button
                color="primary"
                onClick={applyDateFilter}
                style={{
                  height: 32,
                  marginTop: 18,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Filter
              </Button>

              <Button
                outline
                color="secondary"
                onClick={resetDateFilter}
                style={{
                  height: 32,
                  marginTop: 18,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Reset
              </Button>

              {permissions?.View && (
                <Button
                  outline
                  color="primary"
                  onClick={openModal}
                  title="Filter Columns"
                  style={{
                    height: 32,
                    marginTop: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaFilter size={14} />
                </Button>
              )}

              {permissions?.Download && (
                <div
                  className="dl-menu"
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ marginTop: 18 }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="#0b6bcb"
                  />
                  {downloadOpen && (
                    <div className="dl-pop">
                      <div className="dl-item" onClick={handleExportPDF}>
                        .PDF
                      </div>
                      <div
                        className="dl-item"
                        onClick={() => handleExportXLSX("OutwardStock")}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ===== Table ===== */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="os-table-wrap">
                <table className="os-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r, idx) => (
                      <tr key={`${r?._id || r?.id || idx}`}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r, idx)
                              : toStr(c.get(r, idx))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      {visibleCols.map((c, idx) => {
                        if (c.key === "_actions_") return <td key={c.key}></td>;
                        if (c.key === "sno") return <td key={c.key}>Total</td>;
                        if (c.key === "qty")
                          return <td key={c.key}>{fmt2(totals.qty)}</td>;
                        if (c.key === "salesRate")
                          return <td key={c.key}>{fmt2(totals.salesRate)}</td>;
                        if (c.key === "basicTotal")
                          return <td key={c.key}>{fmt2(totals.basicTotal)}</td>;
                        if (c.key === "gstRate")
                          return <td key={c.key}>{fmt2(totals.gstRate)}</td>;
                        if (c.key === "total")
                          return <td key={c.key}>{fmt2(totals.total)}</td>;
                        return <td key={c.key}></td>;
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* ===== Column Chooser ===== */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  maxHeight: 280,
                  overflow: "auto",
                  background: "#fff",
                  padding: 8,
                }}
              >
                {baseColumns.map((col) => (
                  <div
                    key={col.key}
                    style={{
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid #e5e7eb",
                      marginBottom: 6,
                      background: "#fff",
                    }}
                  >
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    maxHeight: 280,
                    overflow: "auto",
                    background: "#fff",
                    padding: 8,
                    flex: 1,
                  }}
                >
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                          marginBottom: 6,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* ===== Detail / Challan Modal ===== */}
      <Modal
        isOpen={viewModal}
        toggle={closeViewModal}
        size="lg"
        style={{ maxWidth: 1200 }}
      >
        <ModalHeader toggle={closeViewModal}>
          {viewData?.fromSales
            ? "Sales Dispatch (Outward)"
            : "Stock Transfer (Outward)"}
        </ModalHeader>
        <ModalBody>
          {!viewData ? null : viewData?.fromSales ? (
            <>
              <Row className="mb-2">
                <Col lg="3" md="4" sm="6">
                  <Label>Party:</Label>
                  <div>{viewData?.row?.PartyName || "-"}</div>
                </Col>
                <Col lg="3" md="4" sm="6">
                  <Label>Date:</Label>
                  <div>{asDate(viewData?.row?.order?.createdAt)}</div>
                </Col>
                <Col lg="3" md="4" sm="6">
                  <Label>Product:</Label>
                  <div>
                    {safeGet(viewData?.row, "productId.Product_Title", "-")}
                  </div>
                </Col>
                <Col lg="3" md="4" sm="6">
                  <Label>Qty:</Label>
                  <div>{viewData?.row?.qty}</div>
                </Col>
              </Row>
            </>
          ) : (
            <>
              <Row className="mb-2">
                <Col lg="3" md="4" sm="6">
                  <Label>From:</Label>
                  <div>
                    {safeGet(viewData, "warehouseFromId.warehouseName", "-")}
                  </div>
                </Col>
                <Col lg="3" md="4" sm="6">
                  <Label>To:</Label>
                  <div>
                    {safeGet(viewData, "warehouseToId.warehouseName", "-")}
                  </div>
                </Col>
                <Col lg="3" md="4" sm="6">
                  <Label>Date:</Label>
                  <div>{asDate(viewData?.stockTransferDate)}</div>
                </Col>
                <Col lg="3" md="4" sm="6">
                  <Label>Status:</Label>
                  <div>
                    <Badge
                      color={
                        /completed/i.test(viewData?.transferStatus)
                          ? "success"
                          : "warning"
                      }
                    >
                      {viewData?.transferStatus}
                    </Badge>
                  </div>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col className="d-flex gap-2">
                  <Button
                    color="primary"
                    onClick={() => downloadChallanPDF(viewData)}
                  >
                    Download Challan (.PDF)
                  </Button>
                  <Input
                    type="select"
                    style={{ maxWidth: 220 }}
                    value=""
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v && v !== "NA") updateStatus(viewData, v);
                    }}
                  >
                    <option value="NA">-- Update Status --</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                    <option value="Hold">Hold</option>
                  </Input>
                </Col>
              </Row>

              <Row>
                <Col>
                  <div className="d-flex justify-content-center">
                    <h5 className="m-0">Product Details</h5>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Table responsive hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>Primary Unit</th>
                        <th>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewData?.productItems || []).map((p, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{safeGet(p, "productId.Product_Title", "-")}</td>
                          <td>{p?.primaryUnit || "-"}</td>
                          <td>{p?.transferQty ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
